import { MessageType, PartialMessage } from '@protobuf-ts/runtime'
import { decodeRefString } from 'lib/websocket-notify'
import { useRouter } from 'next/router'
import { Duration } from 'pbts/google/protobuf/duration'
import { useCallback, useMemo, useState } from 'react'

export const MetaFields = ['pageSize', 'pageToken', 'expandMask', 'sortBy'] as const
export type MetaFields = (typeof MetaFields)[number]

type ResolveI<I extends MessageType<object>> = I extends MessageType<object> ? I : never
export const getNonMetaFields = <I extends MessageType<object>>(i: ResolveI<I>): ResolveI<I>['fields'] =>
    i.fields.filter((f) => !MetaFields.includes(f.localName as MetaFields)).filter((f) => !!f)

export type MessageTypeType<T> = T extends MessageType<infer R> ? R : never

// TODO: this probably needs to be recursive
// NOTE: we don't seem to have enough run time info on field types to know eg numberFields
export const useInitialFilters = <T extends MessageType<object>>(
    i: T,
    numberFields?: Array<keyof MessageTypeType<T>>,
    urlDecode?: (k: string, v: unknown) => unknown,
    durationFields?: Array<keyof MessageTypeType<T>>,
    refFields?: Array<keyof MessageTypeType<T>>,
): MessageTypeType<T> => {
    const router = useRouter()
    const query = router.query
    const mapped: Partial<MessageTypeType<T>> = {}

    for (const f of i.fields || []) {
        if (MetaFields.includes(f.localName as MetaFields)) {
            continue
        }

        let value = query[f.localName]
        if (!value) {
            continue
        }

        if (f.repeat && !Array.isArray(value)) {
            value = [value]
        }
        if (urlDecode) {
            value = urlDecode(f.localName, value) as string
        }

        if (durationFields?.includes(f.localName as keyof MessageTypeType<T>)) {
            if (isNaN(parseInt(value as string, 10))) {
                continue
            }
            const seconds = parseInt(value as string, 10)
            const durationValue: Duration = Duration.create({ seconds })
            mapped[f.localName] = durationValue
            continue
        }

        if (numberFields?.includes(f.localName as keyof MessageTypeType<T>)) {
            if (Array.isArray(value)) {
                mapped[f.localName] = value.map((u) => parseInt(u, 10))
            } else {
                mapped[f.localName] = parseInt(value, 10)
            }
            continue
        }

        if (refFields?.includes(f.localName as keyof MessageTypeType<T>)) {
            if (Array.isArray(value)) {
                mapped[f.localName] = value.map((u) => decodeRefString(u))
            } else {
                mapped[f.localName] = decodeRefString(value)
            }
            continue
        }

        mapped[f.localName] = value
    }

    // componentDidMount equivalent
    return useState(() => i.create(mapped) as MessageTypeType<T>)[0]
}

export type Queryable = { query: string }
export type Actual<T extends Queryable> = T extends Queryable ? T & Queryable : never

export const useSearch = <T extends { query: string }>(
    request: MessageType<Actual<T>>,
    filter: Actual<T>,
    setFilter: (f: (old: Actual<T>) => Actual<T>) => void,
    emptyFilter: PartialMessage<Actual<T>> = {},
    urlEncode?: (k, v) => string | Array<string>,
    unclearableParams?: Array<keyof Actual<T>>,
): {
    onChange: (updated: PartialMessage<Actual<T>>, ignoreMe?: boolean) => void
    canClear: boolean
    clear?: () => void
    onQueryChange: (q: string, isloading) => void
} => {
    type ActualT = Actual<T>
    type PartialT = PartialMessage<ActualT>

    const { urlableParams } = useMemo(() => {
        //  proto -> TS doesn't use types for this: FieldInfo isn't generic!!
        const nonMetaFields = getNonMetaFields(request).map((f) => f.localName) as Array<keyof ActualT>
        return {
            nonMetaFields,
            urlableParams: nonMetaFields.filter((s) => !!s).filter((s) => !(s in emptyFilter)),
        }
    }, [emptyFilter, request])

    const { canClear, clear } = useMemo(() => {
        const hasValidContent = urlableParams.some((localName) => {
            if (unclearableParams?.includes(localName)) return false

            const maybeA = filter[localName]

            if (Array.isArray(maybeA)) {
                return maybeA.length
            }
            return !!filter[localName]
        })

        if (!hasValidContent) {
            return { canClear: hasValidContent, clear: undefined }
        }

        const clearFunc = (): void => {
            const url = new URL(window.location.toString())
            for (const k of urlableParams) {
                url.searchParams.delete(k as string)
            }
            window.history.pushState({}, '', url)
            setFilter(() => request.create(emptyFilter))
        }
        return { canClear: hasValidContent, clear: clearFunc }
    }, [filter, setFilter, emptyFilter, urlableParams, request, unclearableParams])

    const onChange = useCallback(
        (update, ignoreMe): void => {
            if (ignoreMe) {
                // Some old style filters attempt to load data from the URL, which is now handled by useInitialFilters
                return
            }

            let newFilters: ActualT | undefined
            setFilter((old) => {
                const filteredUpdate = Object.fromEntries(
                    Object.entries(update).filter(([, v]) => v !== undefined),
                ) as PartialMessage<Actual<T>>

                if (!old) {
                    newFilters = request.create(filteredUpdate)
                    return newFilters
                }
                const maybeNextFilters = old ? request.clone(old) : request.create()

                // For fields marked as undefined, reset them to their default protobuf values so field
                // comparison can be performed in .equals()
                Object.keys(update).filter((k) => update[k] === undefined).forEach((k) => {
                    const field = request.fields.find((f) => f.localName === k)
                    if (field) {
                        if (field.repeat) {
                            maybeNextFilters[k] = []
                        } else {
                            delete maybeNextFilters[k]
                        }
                    }
                })

                // Filter out undefined values from update otherwise undefined values will be merged
                request.mergePartial(maybeNextFilters, filteredUpdate)
                if (request.equals(maybeNextFilters, old)) {
                    return old
                }
                newFilters = maybeNextFilters
                if ('pageToken' in newFilters) {
                    newFilters.pageToken = ''
                }
                return newFilters
            })

            if (!newFilters) {
                return
            }

            const url = new URL(window.location.toString())
            for (const k of urlableParams) {
                url.searchParams.delete(k as string)
                const v = (newFilters as ActualT)[k as keyof ActualT]

                if (!v) {
                    continue
                }

                if (Array.isArray(v) && !v.length) {
                    continue
                }

                const newV = urlEncode ? urlEncode(k, v) : v

                if (Duration.is(newV)) {
                    url.searchParams.set(k as string, newV.seconds.toString())
                    continue
                }

                if (typeof newV === 'string') {
                    url.searchParams.set(k as string, newV)
                    continue
                }

                if (Array.isArray(newV)) {
                    for (const value of newV) {
                        url.searchParams.append(k as string, value)
                    }
                }
            }
            // This should never happen unless we update the url externally...
            if (window.location.toString() === url.toString()) {
                return
            }
            window.history.pushState({}, '', url)
        },
        [setFilter, urlableParams, request, urlEncode],
    )

    const onQueryChange = useCallback(
        (query: string, isLoading?: boolean): void => {
            onChange({ query } as PartialT, isLoading)
        },
        [onChange],
    )

    return { onChange, canClear, clear, onQueryChange }
}
